import moment from "moment";
import client from "src/services/client";
import { joinErrorMessages } from "src/services/helpers";
import {
	csvError,
	csvErrorData,
	errorMessage,
	loading,
	toastMessage,
} from "src/store/store";
import { navigate } from "svelte-routing";
import Swal from "sweetalert2";

export function resDataToArr(obj) {
	return Object.keys(obj).map((val) => {
		return {
			field: val,
			value: obj[val],
			required: true,
		};
	});
}

export function dynamicSort(property) {
	let sortOrder = 1;

	if (property[0] === "-") {
		sortOrder = -1;
		property = property.substr(1);
	}

	return function (a, b) {
		if (sortOrder == -1) {
			return b[property].localeCompare(a[property]);
		} else {
			return a[property].localeCompare(b[property]);
		}
	};
}

// Formats an error the way extractErrorMessage shows it, without touching the
// errorMessage store — for callers that need the text itself. Returns
// undefined for values extractErrorMessage ignores.
export function formatErrorMessage(error) {
	if (Array.isArray(error)) {
		return error;
	}

	if (typeof error === "string") {
		return `Error: ${error}`;
	}

	if (typeof error === "object") {
		const { response, message } = error;

		if (!response) {
			return `Error: ${message}`;
		}

		const { data, status } = response;

		if (data?.data) {
			return joinErrorMessages(data.data, data?.code || status);
		}
		if (data?.errors) {
			return joinErrorMessages(data.errors, data?.status || status);
		}
		if (data?.message) {
			return `Error ${data?.code || status} : ${data.message}`;
		}
		if (data?.CaseNo) {
			return `Error ${status} : ${data.CaseNo}`;
		}
		return `Error ${status} : ${message}`;
	}

	return undefined;
}

export function extractErrorMessage(error) {
	const formatted = formatErrorMessage(error);
	if (formatted !== undefined) {
		errorMessage.set(formatted);
	}
}

export function handleCsvImport(e, url, service, dataCategory = "Data") {
	Swal.fire({
		title: "Import CSV",
		html: `Do you want to import  <span class="link-text">${e.target.files[0].name}</span>?`,
		showDenyButton: true,
		confirmButtonText: "Import",
		denyButtonText: "Cancel",
		reverseButtons: true,
		customClass: {
			popup: "csv-import-modal",
			title: "csv-import-modal-title",
			htmlContainer: "csv-import-modal-body",
			actions: "csv-import-modal-actions",
			confirmButton: "csv-import-modal-confirm",
			denyButton: "csv-import-modal-cancel",
		},
	}).then((result) => {
		/* Read more about isConfirmed, isDenied below */
		if (result.isConfirmed) {
			const dataArray = new FormData();
			dataArray.append("csvfile", e.target.files[0]);
			dataArray.append("File", e.target.files[0]);
			client(url, {
				method: "POST",
				version: service,
				header: "multipart",
				data: dataArray,
			})
				.then((res) => {
					if (
						res.data.data?.rejects?.length === 0 ||
						(res.data.totalSuccess &&
							res.data.totalSuccess !== 0) ||
						res.data.error_msg === "Success"
					) {
						toastMessage.set({
							message: `CSV imported succesfully`,
							type: "success",
						});
						setTimeout(() => globalThis.location.reload(), 1500);
						e.target.value = "";
					} else {
						let error;
						if (res.data.data?.rejects) {
							error = `${res.data.error_code}: `;
							res.data.data.rejects.forEach((val) => {
								error += `Line Number ${val.lineNumber}: ${val.errorMessage} \n`;
							});
						}
						Swal.fire({
							title: "CSV could not be imported",
							text: error || res.data.message,
							confirmButtonText: "Close",
							customClass: {
								popup: "csv-import-modal",
								title: "csv-import-modal-title",
								htmlContainer: "csv-import-modal-body",
								actions: "csv-import-modal-actions",
								confirmButton: "csv-import-modal-confirm",
							},
						});
						e.target.value = "";
						extractErrorMessage(error || res.data.message);
					}
				})
				.catch((err) => {
					Swal.fire({
						title: "CSV could not be imported",
						text: "Please check your file and try again.",
						confirmButtonText: "Close",
						customClass: {
							popup: "csv-import-modal",
							title: "csv-import-modal-title",
							htmlContainer: "csv-import-modal-body",
							actions: "csv-import-modal-actions",
							confirmButton: "csv-import-modal-confirm",
						},
					});
					e.target.value = "";
					extractErrorMessage(err);
				});
		} else if (result.isDenied) {
			e.target.value = "";
		}
	});
}

export const renderDuration = (obj) => {
	let str = "";
	for (const key in obj) {
		const element = obj[key];
		if (element > 0) {
			str += `${element} ${key} `;
		}
	}
	return str;
};

export const renderBookingStatus = (int) => {
	let status = "";
	switch (int) {
		case 0:
			status = "Confirmed";
			break;
		case 1:
			status = "Ongoing";
			break;
		case 2:
			status = "Cancelled";
			break;
		case 3:
			status = "Cancelled (GetGO)";
			break;
		case 4:
			status = "Completed";
			break;
		case -1:
			status = "Pending";
	}
	return status;
};

export const splitCamelCaseAndCapitalize = (inputString = "") => {
	// Use a regular expression to split CamelCase and capitalize the first letter of each word
	if (typeof inputString === "string")
		return inputString
			.replace(/([a-z])([A-Z])/g, "$1 $2")
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	else {
		return "";
	}
};

//function to split imageUrls and get the folder + file name
export const splitImageUrl = (arr?: string): string => {
	// Handle undefined, null, or empty string
	if (!arr || typeof arr !== "string") return "";
	// Handle empty string after trim
	const trimmedUrl = arr.trim();
	if (!trimmedUrl) return "";
	const segments = trimmedUrl
		.split("/")
		.filter((segment) => segment.length > 0);
	// Remove common base segments that are not meaningful
	const baseSegmentsToSkip = [
		"http:",
		"https:",
		segments.find((s) => s.includes(".amazonaws.com")), // AWS domain
		segments.find((s) => s.includes(".s3")), // S3 references
		segments.find((s) => s.includes("public")), // public buckets
		"fms", // common prefix
	].filter(Boolean);
	// Find the start index after skipping base segments
	let startIndex = 0;
	for (let i = 0; i < segments.length; i++) {
		const segment = segments[i];
		const shouldSkip = baseSegmentsToSkip.some(
			(skipSegment) =>
				segment === skipSegment ||
				segment.includes(".com") ||
				segment.includes(".amazonaws") ||
				segment === "fms"
		);
		if (!shouldSkip) {
			startIndex = i;
			break;
		}
	}
	// Get meaningful segments (skip common infrastructure parts)
	const meaningfulSegments = segments.slice(startIndex);
	if (trimmedUrl.includes("/fms/jobs/") && meaningfulSegments.length >= 5) {
		return meaningfulSegments.slice(-5).join("/");
	}
	// If we have meaningful segments
	if (meaningfulSegments.length > 0) {
		// If it's a simple structure (only 2 segments: folder + filename), return just filename
		if (meaningfulSegments.length === 2) {
			return meaningfulSegments[meaningfulSegments.length - 1];
		}
		// If it's a complex structure (more than 2 segments), return full meaningful path
		else {
			return meaningfulSegments.join("/");
		}
	}
	// Fallback: return just the filename
	if (segments.length >= 1) {
		const filename = segments[segments.length - 1];
		return filename;
	}
	return "";
};

export const importCsv = async (
	e,
	url,
	service,
	close,
	refetch,
	emptyStore
) => {
	// close to toggle modal
	// errorMsgHandler to show error in error modal
	try {
		loading.set(true);
		const dataArray = new FormData();
		dataArray.append("csvfile", e.target.files[0]);
		dataArray.append("File", e.target.files[0]);
		const res = await client(url, {
			method: "POST",
			version: service,
			header: "multipart",
			data: dataArray,
		});
		if (res.data.data?.rejects.length > 0) {
			csvErrorData.set(res.data.data.rejects);
			csvError.set(true);
		} else {
			refetch();
			toastMessage.set({
				message: "The CSV file has been uploaded successfully",
				type: "success",
			});
		}
		close();
		loading.set(false);
		refetch();
		emptyStore();
	} catch (error) {
		extractErrorMessage(error);
		console.log(error);
		loading.set(false);
	}
};

export function queryArrStrToNum(str) {
	if (!str) return [Number.NaN];
	return str.split(",").map(Number);
}

export function arrayToQueryParams(params) {
	let queryParams = {};
	Object.keys(params).forEach((key) => {
		if (Array.isArray(params[key])) {
			queryParams[key] = params[key].join(",");
		} else {
			queryParams[key] = params[key];
		}
	});
	return queryParams;
}

export function capitalize(str) {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

export function lookupToOptions(items) {
	if (items)
		return items.map((item) => {
			return {
				id: item.id,
				label: item.substatus
					? item.status + " - " + item.substatus
					: item.status ||
						item.name ||
						item.description ||
						item.estateName,
				selected: false,
			};
		});
}

export function earliestBooking(start) {
	const now: Date = new Date();
	const startDate: Date = new Date(start);
	const diffMs: number = startDate.getTime() - now.getTime();

	if (diffMs <= 0) {
		return "Already started";
	}

	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
	const diffHours = Math.floor(
		(diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
	);
	const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

	if (diffDays > 0) {
		return `${diffDays} days from now`;
	} else if (diffHours > 0) {
		return `${diffHours} hours from now`;
	} else {
		return `${diffMinutes} minutes from now`;
	}
}

export function daysElapsed(createdAt) {
	const created = new Date(createdAt);
	const now = new Date();
	const diffMs = now.getTime() - created.getTime();

	// round down to nearest whole number
	return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

export function dateRange(start, end) {
	const startDate = moment(start);
	const endDate = moment(end);

	const startTime = startDate.format("HH:mm");
	const endTime = endDate.format("HH:mm");

	if (startDate.isSame(endDate, "day")) {
		return `${startDate.format("DD MMM YYYY")}, ${startTime} - ${endTime}`;
	} else {
		return `${startDate.format(
			"DD MMM YYYY"
		)}, ${startTime} - ${endDate.format("DD MMM YYYY")} ${endTime}`;
	}
}

export function scrollToTop() {
	const contentContainer = document.querySelector(".content-container");
	if (contentContainer) {
		contentContainer.scrollTo({ top: 0, behavior: "smooth" });
	}
}

export const separateSnakeCase = (str = "") => {
	// if str contains underscores, replace them with spaces
	if (str?.includes("_")) {
		return str.split("_").join(" ");
	}
	return str;
};

export const enumFinder = (options, value) => {
	const found = options.find((option) => option.value === value);
	return found ? found.label : value;
};

export function navigateToDetails(basePath: string, id: string) {
	const fullPath = `${basePath}${id}`;
	navigate(fullPath);
}

export function initializeDropdownOptions(options, initialSelectedIds) {
	return options.map((opt) => ({
		...opt,
		selected: initialSelectedIds.includes(opt.id),
	}));
}

export function updateDropdownOptions(currentOptions, newSelectedIds) {
	return currentOptions.map((opt) => ({
		...opt,
		selected: newSelectedIds.includes(opt.id),
	}));
}

export function getSelectedIds(dropdownOptions) {
	return dropdownOptions.filter((o) => o.selected).map((o) => o.id);
}

export const splitByComma = (arr: string[] = []) => {
	return arr
		.flatMap((item) => item.split(","))
		.map((item) => item.trim())
		.filter((item) => item.length > 0)
		.join(", ");
};

export interface CheckboxOption {
	id: string;
	selected: boolean;
	// ...other fields as needed
}
export function updateCheckboxOptions<
	T extends { id: string | number; selected: boolean },
>(options: T[], paramValue: string | string[] | number[] | undefined): T[] {
	let selectedIds: (string | number)[] = [];

	if (paramValue) {
		if (Array.isArray(paramValue)) {
			selectedIds = paramValue.map((val) => {
				if (typeof val === "number") {
					return val;
				}
				return Number.isNaN(Number(val)) ? val : Number(val);
			});
		} else {
			selectedIds = paramValue
				.split(",")
				.map((val) => (Number.isNaN(Number(val)) ? val : Number(val)));
		}
		return options.map((opt) => ({
			...opt,
			selected: selectedIds.includes(opt.id),
		}));
	} else {
		return options.map((opt) => ({
			...opt,
			selected: false,
		}));
	}
}

export const carAssetOwnersDropdownCheckBoxOptions = (carAssetOwners = []) => {
	return carAssetOwners.map((option) => ({
		label: option.name,
		id: option.id,
		selected: false,
	}));
};

export function getOrDefault<T>(value: T | undefined, defaultValue: T): T {
	return value === undefined ? defaultValue : value;
}

export function optionFinder(options, value) {
	const found = options.find((option) => option.value === value);
	return found || { value: value, label: value };
}

export function usePopstate(callback: () => void) {
	if (typeof globalThis !== "undefined") {
		const handler = () => callback();
		globalThis.addEventListener("popstate", handler);

		// Return a cleanup function
		return () => globalThis.removeEventListener("popstate", handler);
	}
	return () => {};
}

export const videoFileTypes = ["mp4", "mov", "avi", "wmv"];

export const isVideoFile = (filename: string | { name: string }) => {
	if (typeof filename === "object" && filename !== null) {
		filename =
			filename && typeof filename.name === "string" ? filename.name : "";
	}

	const extension = filename?.split(".").pop()?.toLowerCase();
	return extension ? videoFileTypes.includes(extension) : false;
};

export const fileContentChecker = (file) => {
	return typeof file === "object" ? URL.createObjectURL(file) : file;
};

export const CXTSRoles = ["CXCall", "CXEmail", "CXLiveChat", "TSSelfReported"];

export const isCXTSRole = (role: string) => {
	return CXTSRoles.includes(role);
};

export const phase3CaseOrigin = ["Operator", "FQ"];

export const isPhase3CaseOrigin = (origin: string) => {
	return phase3CaseOrigin.includes(origin);
};

export const isOperatorCaseOrigin = (originObj: {
	createdFrom?: string;
	caseOrigin?: string;
}) => {
	return (
		originObj.caseOrigin === "Operator" ||
		originObj.createdFrom === "Operator"
	);
};

export const isFQCaseOrigin = (originObj: {
	createdFrom?: string;
	caseOrigin?: string;
}) => {
	return originObj.caseOrigin === "FQ" || originObj.createdFrom === "FQ";
};

interface GetgoCarparkAddressOption {
	address: string;
	postalCode: string;
	longitude: string;
	latitude: string;
}

interface OneMapCarparkAddressOption {
	ADDRESS: string;
	POSTAL: string;
	LONGITUDE: string;
	LATITUDE: string;
}

const isOneMapOption = (
	item: GetgoCarparkAddressOption | OneMapCarparkAddressOption
): item is OneMapCarparkAddressOption => "ADDRESS" in item;

export const mapDataToAddressOptions = (
	data: (GetgoCarparkAddressOption | OneMapCarparkAddressOption)[]
) => {
	return data.map((item) => {
		if (isOneMapOption(item)) {
			return {
				value: item.ADDRESS,
				label: item.ADDRESS,
				postal: item.POSTAL,
				longitude: item.LONGITUDE,
				latitude: item.LATITUDE,
			};
		}
		return {
			value: item.address,
			label: item.address,
			postal: item.postalCode,
			longitude: item.longitude,
			latitude: item.latitude,
		};
	});
};

export const truncateToDp = (value: number, dp: number): string => {
	const [sign, unsigned] =
		value < 0 ? ["-", (-value).toString()] : ["", value.toString()];
	const [intPart, decPart = ""] = unsigned.split(".");
	const truncatedDecPart = decPart.slice(0, dp).padEnd(dp, "0");
	return `${sign}${intPart}.${truncatedDecPart}`;
};

// Once a workshop has completed its portion, Ops still tracks the real
// PendingCompletion -> Completed progression (it drives their next action),
// but that distinction isn't meaningful to a workshop user — their job is
// done either way. Cancelled is left alone: it isn't part of the completion
// continuum, and relabeling it as "completed" would be actively misleading.
const WORKSHOP_COMPLETED_CONTINUUM = new Set([
	"ReadyForCollection",
	"PendingCompletion",
	"Completed",
]);

export const workshopVisibleWorkOrderStatus = (status: string): string =>
	WORKSHOP_COMPLETED_CONTINUUM.has(status) ? "CompletedByWorkshop" : status;

// InWorkshop -> CompletedByWorkshop is only reachable through the workshop's
// own completion flow (issue outcomes, next step, workshopCompletedAt). A
// plain status change from an Ops dropdown would skip all of that.
export const withoutWorkshopOnlyTransitions = (
	transitions: string[] = []
): string[] =>
	transitions.filter((transition) => transition !== "CompletedByWorkshop");

export function vehicleStatusLabel(option) {
	if (!option) return "";
	const status = (option.status || "").trim();
	const substatus = (option.substatus || "").trim();
	if (status.toLowerCase() === "active") return status;
	if (!substatus || substatus.toLowerCase() === status.toLowerCase())
		return status;
	return `${status} - ${substatus}`;
}

export function normalizeVehicleStatusLabel(text) {
	if (!text) return text;
	const idx = text.indexOf(" - ");
	if (idx === -1) return vehicleStatusLabel({ status: text });
	return vehicleStatusLabel({
		status: text.slice(0, idx),
		substatus: text.slice(idx + 3),
	});
}

export function yesNoToBoolean(value) {
	if (value === "Yes") return true;
	if (value === "No") return false;
	return null;
}

export function omitEmpty(obj) {
	return Object.fromEntries(
		Object.entries(obj).filter(
			([, v]) => v !== "" && v !== undefined && v !== null
		)
	);
}

export function formatDateTime(isoString) {
	if (!isoString) return "-";
	return new Date(isoString).toLocaleString("en-SG", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
		hour12: true,
	});
}

export const canEditIssue = (issue) =>
	!!issue && ["Backlog", "Assigned", "InProgress"].includes(issue.status);
